import { AccelFile, User } from "@axl-dev/school-sdk";
import { ChatMessage, ChatMessageType } from "@axl-dev/school-sdk";
import { Dropdown, Tag } from "antd";
import cn from "classnames";
import { useEffect, useMemo, useState } from "react";

import { useFileProvider } from "@app/shared/lib/hooks";
import { AudioPlayer } from "@app/shared/ui/AudioPlayer/AudioPlayer";
import { DateTime } from "@app/shared/ui/DateTime";
import { Files } from "@app/shared/ui/Files/Files";
import { LinkifyText } from "@app/shared/ui/LinkifyText";
import { Loc } from "@app/shared/ui/Loc";
import { UserAvatar } from "@app/shared/ui/UserAvatar";

import styles from "./GroupChatMessage.module.scss";

type Props = {
  message: ChatMessage;
  previousMessage?: ChatMessage | null;
  nextMessage?: ChatMessage | null;
  mine: boolean;
  showAdminTag?: boolean;
  showAuthor?: boolean;
  showDate?: boolean;
  showAvatar?: boolean;
  canReply?: boolean;
  onReply: (message: ChatMessage) => void;
  onPhotoClick?: (file: AccelFile) => void;
};

function withResize(url: string | undefined, width: number) {
  if (!url || !url.startsWith("http")) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("d", `w${width}`);
    return parsed.toString();
  } catch {
    return url;
  }
}

const ChatPhoto: React.FC<{
  file: AccelFile;
  className?: string;
  onClick?: () => void;
}> = ({ file, className, onClick }) => {
  const fileProvider = useFileProvider();
  const original = fileProvider.getUrl(file) ?? file.url ?? undefined;
  const resized = useMemo(() => withResize(original, 480), [original]);
  const [src, setSrc] = useState(resized ?? original);

  useEffect(() => {
    setSrc(resized ?? original);
  }, [original, resized]);

  if (!src) return null;

  return (
    <img
      src={src}
      alt=""
      className={className}
      onClick={onClick}
      onError={() => {
        if (original && src !== original) setSrc(original);
      }}
    />
  );
};

function isPhotoFile(file: AccelFile) {
  if (file.isImage) return true;
  if (file.mimetype?.startsWith("image/")) return true;
  const name = `${file.extension || ""} ${file.cloudKey || ""} ${file.name || ""}`;
  return /\.(png|jpe?g|gif|webp|bmp|svg|ico)(\?|$)/i.test(name);
}

function sameAuthor(a?: ChatMessage | null, b?: ChatMessage | null) {
  if (!a || !b) return false;
  const aId = a.authorId || a.author?.id;
  const bId = b.authorId || b.author?.id;
  return !!aId && aId === bId;
}

export const GroupChatMessage: React.FC<Props> = ({
  message,
  previousMessage,
  nextMessage,
  mine,
  showAdminTag,
  showAuthor = true,
  showDate = true,
  showAvatar = true,
  canReply = true,
  onReply,
  onPhotoClick,
}) => {
  const isVoice = message.type === ChatMessageType.Voice;
  const attachments = message.downloadableAttachments ?? [];
  const photos = attachments.filter((x) => isPhotoFile(x));
  const files = attachments.filter((x) => !isPhotoFile(x));
  const text = message.text || message.plainText;
  const onlyPhotos =
    photos.length > 0 && !text && files.length === 0 && !message.reply && !isVoice;
  const showDaySplitter =
    !previousMessage ||
    !previousMessage.createdDate ||
    !message.createdDate ||
    !previousMessage.createdDate.isSame(message.createdDate, "day");
  const isLastInGroup = useMemo(() => {
    if (!nextMessage) return true;
    if (!sameAuthor(message, nextMessage)) return true;
    if (!message.createdDate || !nextMessage.createdDate) return true;
    if (!nextMessage.createdDate.isSame(message.createdDate, "day")) return true;
    return nextMessage.createdDate.diff(message.createdDate, "minutes") > 5;
  }, [message, nextMessage]);

  const bubble = (
    <div
      className={cn(
        styles.bubble,
        onlyPhotos && styles.bubblePhotos,
        isVoice && styles.bubbleVoice,
      )}
    >
      {message.reply && (
        <div className={styles.quote}>
          <div className={styles.quoteAuthor}>
            <span>
              {message.reply.author?.fullName || message.reply.author?.email || "—"}
            </span>
            {showAdminTag && !!message.reply.author?.adminId && (
              <Tag color="red" className={styles.adminTag}>
                <Loc word="Group chat.adminTag" default="Admin" />
              </Tag>
            )}
          </div>
          <div className="ellipsis">
            {message.reply.type === ChatMessageType.Voice ? (
              <Loc word="Chat.voiceMessage" default="Voice message" />
            ) : (
              message.reply.text ||
              message.reply.plainText || (
                <Loc word="Attachment" default="Attachment" />
              )
            )}
          </div>
        </div>
      )}
      {isVoice && message.voice && (
        <AudioPlayer
          file={message.voice}
          size="middle"
          downloadable
          className={cn(styles.audioPlayer, "p-8 pt-5 pb-5 bg-gray-4 br-xs flex-1")}
        />
      )}
      {photos.length > 0 && (
        <div className={cn(styles.photos, photos.length > 1 && styles.photosGrid)}>
          {photos.map((photo) => (
            <ChatPhoto
              key={photo.id || photo.cloudKey}
              file={photo}
              className={styles.photo}
              onClick={() => onPhotoClick?.(photo)}
            />
          ))}
        </div>
      )}
      {text && (
        <div className={styles.text}>
          <LinkifyText text={text} wrap={false} />
        </div>
      )}
      {files.length > 0 && (
        <div className={styles.files}>
          <Files
            files={files}
            editable={false}
            deletable={false}
            view="grid"
            size="md"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.wrap}>
      {showDaySplitter && message.createdDate && (
        <div className={styles.daySplitter}>
          <DateTime date={message.createdDate} format="ll" className={styles.day} />
        </div>
      )}
      <div
        className={styles.message}
        data-direction={mine ? "out" : "in"}
        data-last-in-group={isLastInGroup}
      >
        {canReply ? (
          <Dropdown
            trigger={["contextMenu"]}
            menu={{
              items: [
                {
                  key: "reply",
                  label: (
                    <Loc word="Chat.replyMessageMsg" default="Reply" />
                  ),
                },
              ],
              onClick: ({ key }) => {
                if (key === "reply") onReply(message);
              },
              style: { width: 150 },
            }}
          >
            {bubble}
          </Dropdown>
        ) : (
          bubble
        )}
        {isLastInGroup && (showAuthor || showDate || showAvatar) && (
          <div className={styles.meta}>
            {showAvatar && !mine && message.author && (
              <UserAvatar
                user={message.author as unknown as User}
                size={24}
                className={styles.avatar}
              />
            )}
            {showDate && (
              <>
                <DateTime
                  date={message.createdDate}
                  format="ll"
                  className={styles.date}
                />
                <DateTime
                  date={message.createdDate}
                  format="LT"
                  className={cn(styles.date, "ml-5")}
                />
              </>
            )}
            {showAuthor && (
              <div className={cn(styles.author, "ellipsis")}>
                <span>
                  {message.author?.fullName || message.author?.email || "—"}
                </span>
                {showAdminTag && !!message.author?.adminId && (
                  <Tag color="red" className={styles.adminTag}>
                    <Loc word="Group chat.adminTag" default="Admin" />
                  </Tag>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
